import { useCallback, useEffect, useRef } from "react";

/**
 * Infinite scroll hook backed by IntersectionObserver.
 *
 * Returns a callback ref to attach to a sentinel element placed at the end of
 * the list. Whenever that sentinel scrolls into view, `loadMore` fires — but
 * only while `hasMore` is true and `isLoading` is false, so we never fire
 * duplicate page requests or run past the last page.
 *
 * A callback ref (rather than useEffect + useRef) is used deliberately: the
 * sentinel is usually rendered conditionally on `hasMore`, so it mounts/unmounts
 * over the component's life. React invokes the callback ref on every such
 * mount/unmount, which keeps the observer correctly attached. Because the ref
 * is also re-created when `hasMore`/`isLoading` change, the observer re-checks
 * visibility after each fetch — so it keeps paginating even when the sentinel
 * stays within the viewport across loads.
 *
 * @param {Object}   params
 * @param {boolean}  params.hasMore   - more pages remain to fetch
 * @param {boolean}  params.isLoading - a fetch is currently in flight
 * @param {Function} params.loadMore  - called to fetch the next page
 * @param {string}   [params.rootMargin] - prefetch distance before the sentinel is visible
 * @returns {Function} callback ref to attach to the sentinel element
 */
const useInfiniteScroll = ({
  hasMore,
  isLoading,
  loadMore,
  rootMargin = "200px",
}) => {
  const observerRef = useRef(null);

  // Always call the latest loadMore without forcing the callback ref to
  // re-create on every render (loadMore is a fresh closure each time).
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const setSentinel = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node || !hasMore) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && !isLoading) {
            loadMoreRef.current();
          }
        },
        { rootMargin }
      );
      observerRef.current.observe(node);
    },
    [hasMore, isLoading, rootMargin]
  );

  // Disconnect on unmount.
  useEffect(
    () => () => {
      if (observerRef.current) observerRef.current.disconnect();
    },
    []
  );

  return setSentinel;
};

export default useInfiniteScroll;
